import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import UserAccessTable from "../../components/UserAccessTable";
import { Warning } from "@phosphor-icons/react";

/**
 * Customer Portal → Profile → User Access
 * ---------------------------------------
 * Only `customer_admin` users see the management table. `customer_viewer`
 * sees a read-only summary of their own row (returned by
 * /api/customer-portal/user-access/me).
 */
export default function CustomerUserAccess() {
  const { user } = useAuth();
  const [role, setRole] = useState(null);           // "customer_admin" | "customer_viewer"
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Two loaders:
  // - `initialLoad`: first time — flips `loading` on so the whole page
  //   shows a spinner (and unmounts children while data is unknown).
  // - `refresh`   : subsequent reloads — quietly re-fetches without
  //   touching `loading`, so the child <UserAccessTable /> stays mounted
  //   and the Activation Link Modal survives the round-trip.
  const _fetch = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await api.get("/customer-portal/user-access");
      setRole("customer_admin");
      setUsers(data.users || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        try {
          const { data } = await api.get("/customer-portal/user-access/me");
          setRole("customer_viewer");
          setMe(data);
          setUsers([data]);
        } catch (e2) {
          setErr(e2?.response?.data?.detail || "Could not load user access.");
        }
      } else {
        setErr(e?.response?.data?.detail || "Could not load user access.");
      }
    }
  }, []);

  const initialLoad = useCallback(async () => {
    setLoading(true);
    try { await _fetch(); } finally { setLoading(false); }
  }, [_fetch]);

  useEffect(() => { initialLoad(); }, [initialLoad]);

  if (loading) return <div className="p-6 text-slate-500" data-testid="portal-user-access-loading">Loading…</div>;
  if (err) return (
    <div className="p-6" data-testid="portal-user-access-error">
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 flex items-start gap-2 text-sm text-rose-800">
        <Warning size={18} weight="bold" className="mt-[1px] shrink-0" /> {err}
      </div>
    </div>
  );

  if (role === "customer_viewer") {
    return (
      <div className="p-6 space-y-4" data-testid="portal-user-access-viewer">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Your portal access</h1>
          <p className="text-sm text-slate-500 mt-1">
            You have viewer access. Only administrators for your company can add or manage portal users.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 max-w-md space-y-2">
          <Row label="Full Name" value={[me?.first_name, me?.last_name].filter(Boolean).join(" ") || "—"} />
          <Row label="Email" value={me?.email} mono />
          <Row label="Role" value={me?.role} />
          <Row label="Status" value={me?.status} />
          <Row label="Login Method" value={me?.login_method || "google"} />
          <Row label="Last Login" value={me?.last_login_at ? new Date(me.last_login_at).toLocaleString() : "Never"} mono />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="portal-user-access-admin">
      <UserAccessTable
        api={api}
        basePath="/customer-portal/user-access"
        addPath="/customer-portal/user-access"
        canManage
        title="User access"
        subtitle="Invite additional people to your customer portal. Admins can manage all users; viewers only see their own profile."
        users={users}
        refresh={_fetch}
        testIdPrefix="portal-users"
      />
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-slate-100 last:border-b-0 gap-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 pt-1">{label}</div>
      <div className={"text-sm text-slate-900 text-right " + (mono ? "font-mono" : "")}>{value || "—"}</div>
    </div>
  );
}
